/**
 * Workflow Search Menu Component
 * 
 * A reusable search menu that can be embedded in each workflow type component
 * Provides context-aware search with Mantine UI and Tabulator
 * Positioned in the lower half of the component
 */

import {
    ActionIcon,
    Affix,
    Alert,
    Badge,
    Box,
    Button,
    Card,
    Collapse,
    Drawer,
    Grid,
    Group,
    Paper,
    Progress,
    SegmentedControl,
    Select,
    Stack,
    Text,
    TextInput,
    Title,
    Tooltip
} from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import {
    IconChevronDown,
    IconChevronUp,
    IconRefresh,
    IconSearch,
    IconX
} from '@tabler/icons-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TabulatorFull } from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator_midnight.min.css';
import { useWorkflowSearch } from '../data/hooks/useWorkflowSearch';

// Import luxon for datetime formatting - make it available globally for Tabulator
try {
    const luxon = require('luxon');
    if (typeof window !== 'undefined') {
        (window as any).luxon = luxon;
    }
} catch {
    // Luxon not available, will use fallback formatter
}

interface SearchMenuItem {
    id: string;
    title: string;
    description: string;
    status?: string;
    tags?: string[];
    created?: string;
    modified?: string;
    [key: string]: any;
}

interface WorkflowSearchMenuProps {
    workflowType: string;
    workflowIcon?: string;
    workflowColor?: string;
    items?: SearchMenuItem[];
    onItemSelect?: (item: SearchMenuItem) => void;
    onSearch?: (query: string, filters: any) => void;
    columns?: any[];
    position?: 'bottom' | 'right';
    height?: string | number;
}

export const WorkflowSearchMenu: React.FC<WorkflowSearchMenuProps> = ({
    workflowType,
    workflowIcon = '📄',
    workflowColor = 'blue',
    items = [],
    onItemSelect,
    onSearch,
    columns,
    position = 'bottom',
    height = '40%'
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedQuery] = useDebouncedValue(searchQuery, 300);
    const [opened, { toggle, close }] = useDisclosure(false);
    const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
    const [sortBy, setSortBy] = useState('recent');
    const [statusFilter, setStatusFilter] = useState<string | null>(null);
    const [filteredItems, setFilteredItems] = useState(items);

    const tableRef = useRef<HTMLDivElement>(null);
    const tableInstance = useRef<InstanceType<typeof TabulatorFull> | null>(null);

    // Use real workflow search instead of mock data
    const { searchResults, loading, error, searchWorkflow } = useWorkflowSearch();

    // Default columns if not provided
    const defaultColumns = [
        {
            title: 'Title',
            field: 'title',
            formatter: 'plaintext',
            sorter: 'string'
        },
        {
            title: 'Description',
            field: 'description',
            formatter: 'textarea',
            width: 300
        },
        {
            title: 'Status',
            field: 'status',
            formatter: (cell: any) => {
                const status = cell.getValue() as string;
                const colors: Record<string, string> = {
                    active: '#40c057',
                    pending: '#fd7e14',
                    completed: '#228be6',
                    failed: '#fa5252',
                    draft: '#868e96'
                };
                return `<span style="
                    background: ${colors[status] || '#868e96'};
                    color: white;
                    padding: 2px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                ">${status || 'unknown'}</span>`;
            },
            width: 100
        },
        {
            title: 'Tags',
            field: 'tags',
            formatter: (cell: any) => {
                const tags = cell.getValue() as string[] || [];
                return tags.map(tag =>
                    `<span style="
                        background: #f1f3f5;
                        color: #495057;
                        padding: 2px 6px;
                        border-radius: 3px;
                        margin-right: 4px;
                        font-size: 11px;
                    ">${tag}</span>`
                ).join('');
            }
        },
        {
            title: 'Modified',
            field: 'modified',
            formatter: (window as any).luxon ? 'datetime' : function (cell: any) {
                const date = cell.getValue();
                if (!date) return '';
                const d = new Date(date);
                return d.toLocaleDateString('en-US', { year: '2-digit', month: '2-digit', day: '2-digit' });
            },
            formatterParams: (window as any).luxon ? {
                outputFormat: 'MM/DD/YY'
            } : undefined,
            width: 100
        }
    ];

    // Initialize or update Tabulator
    useEffect(() => {
        if (!tableRef.current || viewMode !== 'table' || !opened) {
            return undefined;
        }

        const initializeTable = () => {
            if (!tableRef.current) {
                return;
            }

            // Destroy existing instance
            if (tableInstance.current) {
                try {
                    tableInstance.current.destroy();
                } catch (e) {
                    console.warn('Error destroying table:', e);
                }
                tableInstance.current = null;
            }

            // Create new instance
            try {
                tableInstance.current = new TabulatorFull(tableRef.current, {
                    data: filteredItems || [],
                    height: 300,
                    layout: 'fitColumns',
                    responsiveLayout: 'collapse',
                    pagination: true,
                    paginationSize: 5,
                    movableColumns: true,
                    placeholder: "Loading search results...",
                    renderHorizontal: 'virtual',
                    renderVertical: 'virtual',
                    columns: columns || defaultColumns,
                    rowClick: (e: Event, row: any) => {
                        const data = row.getData() as SearchMenuItem;
                        onItemSelect?.(data);
                    }
                });
            } catch (error) {
                console.error('Failed to initialize search table:', error);
            }
        };

        const timer = setTimeout(initializeTable, 100);

        return () => {
            clearTimeout(timer);
            if (tableInstance.current) {
                try {
                    tableInstance.current.destroy();
                } catch (error) {
                    console.warn('Error destroying table in cleanup:', error);
                }
                tableInstance.current = null;
            }
        };
    }, [filteredItems, viewMode, opened, columns, onItemSelect]);

    // Filter items based on search and filters
    useEffect(() => {
        let filtered = [...items];

        // Apply search query
        if (debouncedQuery) {
            filtered = filtered.filter(item =>
                item.title?.toLowerCase().includes(debouncedQuery.toLowerCase()) ||
                item.description?.toLowerCase().includes(debouncedQuery.toLowerCase()) ||
                item.tags?.some(tag => tag.toLowerCase().includes(debouncedQuery.toLowerCase()))
            );
            onSearch?.(debouncedQuery, { status: statusFilter, sortBy });
        }

        // Apply status filter
        if (statusFilter) {
            filtered = filtered.filter(item => item.status === statusFilter);
        }

        // Apply sorting
        if (sortBy === 'recent' && filtered[0]?.modified) {
            filtered.sort((a, b) =>
                new Date(b.modified || 0).getTime() - new Date(a.modified || 0).getTime()
            );
        } else if (sortBy === 'alphabetical') {
            filtered.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        } else if (sortBy === 'status') {
            filtered.sort((a, b) => (a.status || '').localeCompare(b.status || ''));
        }

        setFilteredItems(filtered);
        return undefined;
    }, [debouncedQuery, statusFilter, sortBy, items, onSearch]);

    // Use real search data instead of mock data
    useEffect(() => {
        if (items.length === 0 && debouncedQuery) {
            // Search when query changes
            searchWorkflow(debouncedQuery, {
                type: workflowType.toLowerCase(),
                status: statusFilter || undefined,
                sortBy: sortBy || undefined
            });
        } else if (items.length === 0 && !debouncedQuery) {
            // Load initial data for this workflow type
            searchWorkflow('', {
                type: workflowType.toLowerCase(),
                limit: 20
            });
        }
    }, [debouncedQuery, workflowType, statusFilter, sortBy, items.length, searchWorkflow]);

    // Update filtered items from search results
    useEffect(() => {
        if (searchResults.length > 0) {
            setFilteredItems(searchResults);
        } else if (items.length > 0) {
            // Use provided items if available
            setFilteredItems(items);
        }
    }, [searchResults, items]);

    const refreshData = useCallback(() => {
        // Trigger data refresh from real search
        searchWorkflow(debouncedQuery, {
            type: workflowType.toLowerCase(),
            status: statusFilter || undefined,
            sortBy: sortBy || undefined,
            refresh: true
        });

        // Also call original onSearch if provided
        onSearch?.(debouncedQuery, { refresh: true });
    }, [searchWorkflow, debouncedQuery, workflowType, statusFilter, sortBy, onSearch]);

    if (position === 'right') {
        // Render as a drawer on the right side
        return (
            <>
                <Affix position={{ bottom: 20, right: 20 }}>
                    <Button
                        leftSection={<IconSearch size={18} />}
                        onClick={toggle}
                        variant="filled"
                        color={workflowColor as any}
                    >
                        Search {workflowType}
                    </Button>
                </Affix>

                <Drawer
                    opened={opened}
                    onClose={close}
                    position="right"
                    size="50%"
                    title={
                        <Group>
                            <Text size="xl">{workflowIcon}</Text>
                            <Title order={4}>{workflowType} Search</Title>
                        </Group>
                    }
                >
                    {/* Drawer content same as below */}
                </Drawer>
            </>
        );
    }

    // Render as collapsible bottom panel
    return (
        <Box style={{ position: 'relative', marginTop: 'auto' }}>
            {/* Toggle Button */}
            <Paper
                withBorder
                style={{
                    cursor: 'pointer',
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                    background: opened ? '#f8f9fa' : 'white'
                }}
                onClick={toggle}
                p="sm"
            >
                <Group justify="space-between">
                    <Group>
                        <Text size="lg">{workflowIcon}</Text>
                        <Text fw={500}>Search & Filter {workflowType}</Text>
                        <Badge color={workflowColor as any} variant="light">
                            {filteredItems.length} items
                        </Badge>
                    </Group>
                    <ActionIcon variant="subtle">
                        {opened ? <IconChevronDown size={20} /> : <IconChevronUp size={20} />}
                    </ActionIcon>
                </Group>
            </Paper>

            {/* Collapsible Search Panel */}
            <Collapse in={opened}>
                <Paper
                    withBorder
                    style={{
                        borderTop: 0,
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        height: typeof height === 'number' ? `${height}px` : height,
                        overflow: 'auto'
                    }}
                    p="md"
                >
                    <Stack gap="md">
                        {/* Search Bar */}
                        <Group>
                            <TextInput
                                placeholder={`Search ${workflowType}...`}
                                leftSection={<IconSearch size={16} />}
                                rightSection={
                                    searchQuery && (
                                        <ActionIcon
                                            size="xs"
                                            variant="subtle"
                                            onClick={() => setSearchQuery('')}
                                        >
                                            <IconX size={14} />
                                        </ActionIcon>
                                    )
                                }
                                value={searchQuery}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.currentTarget.value)}
                                style={{ flex: 1 }}
                            />
                            <Select
                                placeholder="Status"
                                value={statusFilter}
                                onChange={(v) => setStatusFilter(v ?? '')}
                                data={[
                                    { value: 'active', label: 'Active' },
                                    { value: 'pending', label: 'Pending' },
                                    { value: 'completed', label: 'Completed' },
                                    { value: 'failed', label: 'Failed' },
                                    { value: 'draft', label: 'Draft' }
                                ]}
                                clearable
                                w={120}
                            />
                            <Select
                                value={sortBy}
                                onChange={(v: string | null) => setSortBy(v || 'recent')}
                                data={[
                                    { value: 'recent', label: 'Recent' },
                                    { value: 'alphabetical', label: 'A-Z' },
                                    { value: 'status', label: 'Status' }
                                ]}
                                w={120}
                            />
                            <SegmentedControl
                                value={viewMode}
                                onChange={(v: string) => setViewMode(v as 'table' | 'cards')}
                                data={[
                                    { label: 'Table', value: 'table' },
                                    { label: 'Cards', value: 'cards' }
                                ]}
                            />
                            <Tooltip label="Refresh">
                                <ActionIcon onClick={refreshData} variant="default">
                                    <IconRefresh size={18} />
                                </ActionIcon>
                            </Tooltip>
                        </Group>

                        {/* Results */}
                        {loading ? (
                            <Stack ta="center" gap="md" py="xl">
                                <Progress value={30} animated size="sm" style={{ width: '100%' }} />
                                <Text size="sm" c="dimmed">Searching {workflowType.toLowerCase()}s...</Text>
                            </Stack>
                        ) : error ? (
                            <Alert color="red" title="Search Error">
                                <Text>{error.message}</Text>
                                <Button
                                    mt="sm"
                                    size="sm"
                                    variant="outline"
                                    onClick={refreshData}
                                >
                                    Retry
                                </Button>
                            </Alert>
                        ) : viewMode === 'table' ? (
                            <div ref={tableRef} />
                        ) : (
                            <Grid>
                                {filteredItems.map(item => (
                                    <Grid.Col key={item.id} span={4}>
                                        <Card
                                            withBorder
                                            shadow="xs"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => onItemSelect?.(item)}
                                        >
                                            <Group justify="space-between" mb="xs">
                                                <Text fw={500} lineClamp={1}>{item.title}</Text>
                                                {item.status && (
                                                    <Badge
                                                        size="sm"
                                                        color={
                                                            item.status === 'active' ? 'green' :
                                                                item.status === 'pending' ? 'orange' :
                                                                    item.status === 'completed' ? 'blue' :
                                                                        item.status === 'failed' ? 'red' : 'gray'
                                                        }
                                                    >
                                                        {item.status}
                                                    </Badge>
                                                )}
                                            </Group>
                                            <Text size="sm" c="dimmed" lineClamp={2}>
                                                {item.description}
                                            </Text>
                                            {item.tags && item.tags.length > 0 && (
                                                <Group gap={4} mt="xs">
                                                    {item.tags.map((tag, idx) => (
                                                        <Badge key={idx} size="xs" variant="light">
                                                            {tag}
                                                        </Badge>
                                                    ))}
                                                </Group>
                                            )}
                                        </Card>
                                    </Grid.Col>
                                ))}
                            </Grid>
                        )}

                        {/* Quick Stats */}
                        <Group gap="xs">
                            <Text size="sm" c="dimmed">
                                Showing {filteredItems.length} results
                                {searchQuery && ` for "${searchQuery}"`}
                            </Text>
                        </Group>
                    </Stack>
                </Paper>
            </Collapse>
        </Box>
    );
};

export default WorkflowSearchMenu;
